import { useContext } from 'react';
import type { Attachment, AttachmentInput } from 'sr-types/lib/production/v1/graphql';
import { UserContext } from '../auth/UserContext';
import upload, { createLinkFormData, UPLOAD_URL } from '../utils/upload';
import { failedToastMessage } from '../utils/commonUtils';
import type { SaveAttachmentsResponse } from './attachmentUtils';
import { applySavedAttachmentIds, hasAttachmentId, mergeAttachmentsByReference } from './attachmentUtils';

type AttachmentLinkUploaderProps = {
    onSave: (entityReferenceId: string, files: AttachmentInput[]) => void | Promise<SaveAttachmentsResponse | void>;
    localAttachments?: AttachmentInput[];
    setLocalAttachments: React.Dispatch<React.SetStateAction<AttachmentInput[]>>;
    setIsLinkDialogOpen: (open: boolean) => void;
    entityReferenceId?: string;
    setIsSavingLinkAttachment: React.Dispatch<React.SetStateAction<boolean>>;
    onSaveAttachments?: (attachments: Attachment[]) => void;
};

export default function useAttachmentLinkUploader({
    onSave,
    localAttachments,
    setLocalAttachments,
    setIsLinkDialogOpen,
    entityReferenceId,
    setIsSavingLinkAttachment,
    onSaveAttachments
}: AttachmentLinkUploaderProps) {
    const { activeOrganizationAccount, userProfile } = useContext(UserContext);

    const uploadLink = async (url: string, label: string, tags: string[]): Promise<void> => {
        setIsSavingLinkAttachment(true);
        let savedAttachments: Attachment[] = [];

        try {
            const response = await upload(
                activeOrganizationAccount,
                UPLOAD_URL,
                undefined,
                createLinkFormData(url, label)
            );
            const reference = response?.data?.reference;
            if (!reference) {
                return;
            }

            const fileToSave = {
                entityReference: { ...reference, label },
                isLink: true,
                linkUrl: url,
                tags: [...tags],
                name: label,
                createdBy: userProfile?.accountId
            } as AttachmentInput;
            const existingAttachments = localAttachments ?? [];
            const attachmentsToSave = [...existingAttachments, fileToSave];
            const saveResponse = await onSave(entityReferenceId, attachmentsToSave);
            if (!saveResponse) {
                return;
            }

            savedAttachments = applySavedAttachmentIds([fileToSave], saveResponse, attachmentsToSave.length - 1).filter(
                hasAttachmentId
            ) as Attachment[];
            if (savedAttachments.length > 0) {
                setLocalAttachments((prevFiles) => mergeAttachmentsByReference([...prevFiles, ...savedAttachments]));
            }
        } catch (error) {
            failedToastMessage('attachment.link.failed.message');
        } finally {
            setIsLinkDialogOpen(false);
            setIsSavingLinkAttachment(false);
        }

        if (savedAttachments.length > 0) {
            onSaveAttachments?.(savedAttachments);
        }
    };

    return { uploadLink };
}
